import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Form, Checkbox, Radio, message, Input } from 'antd';
import FormField, { FormFieldProps } from 'components/FormField';
import QuickRangePicker from 'components/QuickRangePicker';
import StoreSelector from 'components/StoreSelector';
import { MAX_SELECTED_LENGTH } from 'components/StoreSelector/hooks/useStoreSelector';
import { Formik, FormikConfig, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import dayjs from 'lib/dayjs';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { searchStoreList, StoreInfo } from 'services/api/store';
import { toUpperCase } from 'utils/toLowerCase';
import { quickDays } from '../hooks/useSearch';
import { toNumber, uniq } from 'lodash-es';

const StyledForm = styled(Form)`
  padding: 16px 16px 4px;
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item')} {
    margin-bottom: 12px;
    &-label {
      min-width: 72px;
    }
  }
`;

const StyledStoreSelector = styled(StoreSelector)`
  height: 30px;
  .store-selector-placeholder {
    padding: 0;
  }
`;

type FV = Review.Filte;

export type FilterFormHelpers = FormikHelpers<FV>;

export interface FilterFormProps extends FormikConfig<FV> {
  children?: (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;
}

function FilterForm(props: FilterFormProps) {
  const { children, ...otherProps } = props;

  return (
    <Formik enableReinitialize {...otherProps}>
      {(formProps) => {
        const {
          values: { platform },
        } = formProps;

        const form = (
          <StyledForm>
            <Store />
            <ProductName />
            <TimeRange />
            {
              {
                shopee: <ShopeeStar />,
                lazada: <LazadaStar />,
                mercado: <ShopeeStar />,
                tiktok: <ShopeeStar />,
                tiktok_us: <ShopeeStar />,
              }[platform]
            }
            <Status />
            <HasContent />
          </StyledForm>
        );

        return children ? children(form, formProps) : form;
      }}
    </Formik>
  );
}

function Store() {
  const {
    values: { platform, region, follower, storeId },
    setValues,
  } = useFormikContext<FV>();

  const { t } = useTranslate();

  const [storeList, setStoreList] = useState<StoreInfo[] | undefined>();

  const [loading, setLoading] = useState(false);

  const refsCurrent = {
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if ((!storeId || storeId.length === 0) && storeList && storeList.length) {
      setStoreList([]);

      return;
    }

    if ((storeList && storeList.length) || !(storeId && storeId.length)) {
      return;
    }

    setLoading(true);

    searchStoreList({
      storeIds: storeId.map((id) => parseInt(id, 10)),
      cursor: 0,
      page_size: MAX_SELECTED_LENGTH,
    })
      .then((res) => {
        setStoreList(res.data.data.data);

        setLoading(false);
      })
      .catch(() => {
        message.error(refs.current.t('scene.reviewWorkspace.list.error.fetchStoreListFailed'));

        setLoading(false);
      });
  }, [storeId, storeList]);

  return (
    <Form.Item label={t('scene.reviewWorkspace.list.filter.store')}>
      {loading ? (
        <LoadingOutlined />
      ) : (
        <StyledStoreSelector
          scene="VIEW_REVIEW"
          platformLimit={toUpperCase(platform)}
          displayUnreadCount={false}
          displayTriggerIcon={false}
          defaultTriggerText={`+ ${t('scene.reviewWorkspace.list.filter.selectStore')}`}
          value={{
            region,
            follower,
            storeList,
          }}
          onChange={(value) => {
            const { region, follower, storeList } = value;

            setStoreList(storeList);

            setValues((values) => {
              return {
                ...values,
                region,
                follower,
                storeId: storeList ? storeList.map(({ storeId }) => storeId) : undefined,
              };
            });
          }}
        />
      )}
    </Form.Item>
  );
}

function TimeRange() {
  const {
    values: { reviewStartTime, reviewEndTime },
    setValues,
  } = useFormikContext<FV>();

  const { t } = useTranslate();

  return (
    <Form.Item label={t('scene.reviewWorkspace.list.filter.reviewTime')}>
      <QuickRangePicker
        style={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          lineHeight: '32px',
        }}
        value={
          reviewStartTime && reviewEndTime
            ? [dayjs(parseInt(reviewStartTime, 10)), dayjs(parseInt(reviewEndTime, 10))]
            : undefined
        }
        onChange={(range) => {
          const [start, end] = range || [];

          const reviewStartTime = start ? start.startOf('days').valueOf().toString() : undefined;
          const reviewEndTime = end ? end.endOf('days').valueOf().toString() : undefined;

          setValues((values) => {
            return {
              ...values,
              reviewStartTime,
              reviewEndTime,
            };
          });
        }}
        quickActions={quickDays.map((day) => {
          return {
            key: `${day}`,
            label: t('scene.reviewWorkspace.list.filter.lastXDays', { x: day }),
            range: () => {
              return [
                dayjs()
                  .subtract(day - 1, 'day')
                  .startOf('day'),
                dayjs().endOf('day'),
              ];
            },
          };
        })}
        disabledDate={(date) => {
          if (date.isAfter(dayjs().endOf('days'))) {
            return true;
          }

          if (date.isBefore(dayjs().subtract(180, 'days').startOf('days'))) {
            return true;
          }

          return false;
        }}
      />
    </Form.Item>
  );
}

interface StatusOption {
  status: Review.StringStatus;
  label: ReactNode;
}

function Status() {
  const { t } = useTranslate();

  const statusOptions: StatusOption[] = [
    {
      status: '1',
      label: t('domain.review.status.pending'),
    },
    {
      status: '2',
      label: t('domain.review.status.inProgress'),
    },
    {
      status: '3',
      label: t('domain.review.status.processed'),
    },
    {
      status: '4',
      label: t('domain.review.status.noActionNeeded'),
    },
  ];

  return (
    <FormField<FV['handleStatus']>
      name="handleStatus"
      label={t('scene.reviewWorkspace.list.filter.reviewStatus')}
    >
      {({ field: { value = [], onChange } }) => {
        return (
          <Checkbox.Group
            value={value}
            onChange={(value) => onChange(value.sort() as Review.StringStatus[])}
          >
            {statusOptions.map(({ status, label }) => {
              return (
                <Checkbox key={status} value={status}>
                  {label}
                </Checkbox>
              );
            })}
          </Checkbox.Group>
        );
      }}
    </FormField>
  );
}

function HasContent() {
  const { t } = useTranslate();

  return (
    <FormField<FV['hasContent']>
      name="hasContent"
      label={t('scene.reviewWorkspace.list.filter.reviewMedia')}
    >
      {({ field: { value, onChange } }) => {
        return (
          <Radio.Group
            value={value}
            onChange={({ target: { value } }) => onChange(value as FV['hasContent'])}
          >
            <Radio value="all">{t('scene.reviewWorkspace.list.filter.all')}</Radio>
            <Radio value="1">{t('scene.reviewWorkspace.list.filter.withMedia')}</Radio>
            <Radio value="0">{t('scene.reviewWorkspace.list.filter.noMedia')}</Radio>
          </Radio.Group>
        );
      }}
    </FormField>
  );
}

function StarField(props: FormFieldProps<FV['reviewRating']>) {
  const { t } = useTranslate();

  return (
    <FormField<FV['reviewRating']> {...props}>
      {({ field: { value = [], onChange } }) => {
        const allStars = ['1', '2', '3', '4', '5'];

        return (
          <div>
            <Checkbox
              checked={value.length === allStars.length}
              indeterminate={value.length > 0 && value.length < allStars.length}
              onChange={({ target: { checked } }) => onChange(checked ? allStars : [])}
            >
              {t('scene.reviewWorkspace.list.filter.all')}
            </Checkbox>
            {allStars.map((star) => {
              return (
                <Checkbox
                  key={star}
                  checked={value.includes(star)}
                  onChange={({ target: { checked } }) => {
                    onChange(
                      checked ? uniq([...value, star]).sort() : value.filter((_) => _ !== star),
                    );
                  }}
                >
                  {t('scene.reviewWorkspace.detail.field.xStars', { count: toNumber(star) })}
                </Checkbox>
              );
            })}
          </div>
        );
      }}
    </FormField>
  );
}

function ShopeeStar() {
  const { t } = useTranslate();

  return (
    <StarField name="reviewRating" label={t('scene.reviewWorkspace.list.filter.reviewRating')} />
  );
}

function LazadaStar() {
  const { t } = useTranslate();

  return (
    <>
      <StarField name="reviewRating" label={t('domain.review.field.overallRating')} />
      <StarField name="productRating" label={t('domain.review.field.productRating')} />
      <StarField name="logisticsRating" label={t('domain.review.field.logisticsRating')} />
      <StarField name="sellerRating" label={t('domain.review.field.sellerRating')} />
    </>
  );
}

function ProductName() {
  const { t } = useTranslate();

  return (
    <FormField<FV['spuName']> name="spuName" label={t('scene.reviewWorkspace.list.filter.product')}>
      {({ field: { value, onChange } }) => {
        return (
          <Input
            placeholder={t('scene.reviewWorkspace.list.filter.productName')}
            value={value}
            onChange={(value) => onChange(value)}
            allowClear
          />
        );
      }}
    </FormField>
  );
}

export default FilterForm;
